import PageBanner from '../../../components/shared/PageBanner';
import { Button, Input, InputNumber, Popconfirm, Switch, message } from 'antd';
import { FunctionComponent, useEffect, useMemo, useState } from 'react';
import { deleteRoleDefinition, getRoleDefinitions, RoleDefinition, saveRoleDefinition } from '../../../config/rolePermissions';

const RoleManagementPage: FunctionComponent = () => {
    const [refreshKey, setRefreshKey] = useState<number>(0);
    const roles = useMemo(() => getRoleDefinitions(), [refreshKey]);
    const [selectedRoleId, setSelectedRoleId] = useState<number>(roles[0]?.id ?? 0);
    const selectedRole = useMemo(
        () => roles.find((r) => r.id === selectedRoleId),
        [roles, selectedRoleId],
    );

    const [roleId, setRoleId] = useState<number>(selectedRole?.id ?? 0);
    const [roleName, setRoleName] = useState<string>(selectedRole?.name ?? '');
    const [roleDescription, setRoleDescription] = useState<string>(selectedRole?.description ?? '');
    const [showMenu, setShowMenu] = useState<boolean>(!!selectedRole?.permissions.showMenu);
    const [canManageDocuments, setCanManageDocuments] = useState<boolean>(!!selectedRole?.permissions.canManageDocuments);
    const [canManageRoles, setCanManageRoles] = useState<boolean>(!!selectedRole?.permissions.canManageRoles);
    const [canAccessDataAnalytics, setCanAccessDataAnalytics] = useState<boolean>(!!selectedRole?.permissions.canAccessDataAnalytics);
    const [canAddDocuments, setCanAddDocuments] = useState<boolean>(!!selectedRole?.permissions.canAddDocuments);
    const [canDeleteDocuments, setCanDeleteDocuments] = useState<boolean>(!!selectedRole?.permissions.canDeleteDocuments);

    const [newRoleId, setNewRoleId] = useState<number | null>(null);
    const [newRoleName, setNewRoleName] = useState<string>('');
    const [newRoleDescription, setNewRoleDescription] = useState<string>('');
    const [panelMode, setPanelMode] = useState<'none' | 'edit' | 'create'>('none');
    const roleIdHint = useMemo(
        () => roles.map((r) => `${r.name} (${r.id})`).join(' · '),
        [roles],
    );

    const preventContextMenu = (e: React.MouseEvent) => e.preventDefault();

    useEffect(() => {
        if (!selectedRole) return;
        setRoleId(selectedRole.id);
        setRoleName(selectedRole.name);
        setRoleDescription(selectedRole.description);
        setShowMenu(selectedRole.permissions.showMenu);
        setCanManageDocuments(selectedRole.permissions.canManageDocuments);
        setCanManageRoles(selectedRole.permissions.canManageRoles);
        setCanAccessDataAnalytics(selectedRole.permissions.canAccessDataAnalytics);
        setCanAddDocuments(selectedRole.permissions.canAddDocuments);
        setCanDeleteDocuments(selectedRole.permissions.canDeleteDocuments);
    }, [selectedRole]);

    const handleEditRole = (id: number) => {
        setSelectedRoleId(id);
        setPanelMode('edit');
    };

    const openCreatePanel = () => {
        setNewRoleId(null);
        setNewRoleName('');
        setNewRoleDescription('');
        setPanelMode('create');
        setTimeout(() => {
            document.getElementById('role-create-panel')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 0);
    };

    const saveCurrentRole = () => {
        if (!roleName.trim()) {
            message.error('Role name is required');
            return;
        }
        const updated: RoleDefinition = {
            id: roleId,
            name: roleName.trim(),
            description: roleDescription.trim(),
            permissions: {
                showMenu,
                canManageDocuments,
                canManageRoles,
                canAccessDataAnalytics,
                canAddDocuments,
                canDeleteDocuments,
            },
        };
        saveRoleDefinition(updated);
        setRefreshKey((k) => k + 1);
        setSelectedRoleId(updated.id);
        message.success('Role updated');
    };

    const addRole = () => {
        const id = Number(newRoleId);
        if (Number.isNaN(id)) {
            message.error('Role ID is required');
            return;
        }
        if (!newRoleName.trim()) {
            message.error('Role name is required');
            return;
        }
        if (roles.some((r) => r.id === id)) {
            message.error('Role ID already exists');
            return;
        }

        const role: RoleDefinition = {
            id,
            name: newRoleName.trim(),
            description: newRoleDescription.trim(),
            permissions: {
                showMenu: false,
                canManageDocuments: false,
                canManageRoles: false,
                canAccessDataAnalytics: false,
                canAddDocuments: false,
                canDeleteDocuments: false,
            },
        };
        saveRoleDefinition(role);
        setRefreshKey((k) => k + 1);
        setSelectedRoleId(role.id);
        setNewRoleId(null);
        setNewRoleName('');
        setNewRoleDescription('');
        setPanelMode('edit');
        message.success('Role added');
    };

    const handleDeleteRole = (id: number) => {
        if (roles.length <= 1) {
            message.error('At least one role must remain');
            return;
        }
        deleteRoleDefinition(id);
        const nextRoles = getRoleDefinitions();
        setSelectedRoleId(nextRoles[0]?.id ?? 0);
        setPanelMode('none');
        setRefreshKey((k) => k + 1);
        message.success('Role deleted');
    };

    return (
        <div className="documents-page documents-page--with-app-header documents-page--roles" onContextMenu={preventContextMenu}>
            <div className="documents-page__inner">
                <PageBanner title="Role Management" storageKey="banner-role-management">
                    <div className="documents-page__subtitle">Manage role metadata and role permissions.</div>
                </PageBanner>

                {panelMode === 'none' ? (
                    <div style={{ marginBottom: 12, color: '#6b7280' }}>
                        Select a role and click <strong>Edit</strong>, or click <strong>+ Create New</strong>.
                    </div>
                ) : null}

                <div style={{ display: 'grid', gap: 16, maxWidth: 900 }}>
                    {panelMode === 'edit' ? (
                        <div style={{ border: '1px solid #e5e7eb', borderRadius: 8, background: '#fff', padding: 16, display: 'grid', gap: 12 }}>
                            <h3 style={{ margin: 0, fontSize: 16, color: '#1f2937' }}>Edit Role</h3>
                            <div style={{ fontSize: 13, color: '#6b7280' }}>
                                Selected: {selectedRole?.name || '-'} ({selectedRole?.id ?? '-'})
                            </div>

                            <label htmlFor="role-id">Role ID</label>
                            <InputNumber id="role-id" value={roleId} disabled style={{ width: '100%' }} />
                            <div style={{ fontSize: 12, color: '#6b7280' }}>
                                Available mappings: {roleIdHint}
                            </div>

                            <label htmlFor="role-name">Role Name</label>
                            <Input id="role-name" value={roleName} onChange={(e) => setRoleName(e.target.value)} />

                            <label htmlFor="role-description">Description</label>
                            <Input.TextArea id="role-description" value={roleDescription} onChange={(e) => setRoleDescription(e.target.value)} autoSize={{ minRows: 3, maxRows: 6 }} />

                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 12 }}>
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', border: '1px solid #e5e7eb', borderRadius: 6, padding: '8px 10px' }}>
                                    <span>Show Menu</span>
                                    <Switch checked={showMenu} onChange={setShowMenu} />
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', border: '1px solid #e5e7eb', borderRadius: 6, padding: '8px 10px' }}>
                                    <span>Can Manage Documents</span>
                                    <Switch checked={canManageDocuments} onChange={setCanManageDocuments} />
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', border: '1px solid #e5e7eb', borderRadius: 6, padding: '8px 10px' }}>
                                    <span>Can Manage Roles</span>
                                    <Switch checked={canManageRoles} onChange={setCanManageRoles} />
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', border: '1px solid #e5e7eb', borderRadius: 6, padding: '8px 10px' }}>
                                    <span>Can Access Data Analytics</span>
                                    <Switch checked={canAccessDataAnalytics} onChange={setCanAccessDataAnalytics} />
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', border: '1px solid #e5e7eb', borderRadius: 6, padding: '8px 10px' }}>
                                    <span>Can Add Documents</span>
                                    <Switch checked={canAddDocuments} onChange={setCanAddDocuments} />
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', border: '1px solid #e5e7eb', borderRadius: 6, padding: '8px 10px' }}>
                                    <span>Can Delete Documents</span>
                                    <Switch checked={canDeleteDocuments} onChange={setCanDeleteDocuments} />
                                </div>
                            </div>

                            <Button type="primary" onClick={saveCurrentRole} style={{ width: 'fit-content' }}>
                                Save Role
                            </Button>
                        </div>
                    ) : null}

                    {panelMode === 'create' ? (
                        <div id="role-create-panel" style={{ border: '1px solid #e5e7eb', borderRadius: 8, background: '#fff', padding: 16, display: 'grid', gap: 12 }}>
                            <h3 style={{ margin: 0, fontSize: 16, color: '#1f2937' }}>Add New Role</h3>
                            <label htmlFor="new-role-id">Role ID</label>
                            <InputNumber id="new-role-id" value={newRoleId as number | undefined} onChange={(v) => setNewRoleId(v as number | null)} style={{ width: '100%' }} />
                            <div style={{ fontSize: 12, color: '#6b7280' }}>
                                Existing mappings: {roleIdHint}
                            </div>
                            <label htmlFor="new-role-name">Role Name</label>
                            <Input id="new-role-name" value={newRoleName} onChange={(e) => setNewRoleName(e.target.value)} />
                            <label htmlFor="new-role-description">Description</label>
                            <Input.TextArea id="new-role-description" value={newRoleDescription} onChange={(e) => setNewRoleDescription(e.target.value)} autoSize={{ minRows: 3, maxRows: 6 }} />
                            <Button type="primary" onClick={addRole} style={{ width: 'fit-content' }}>
                                Add Role
                            </Button>
                        </div>
                    ) : null}

                    <div style={{ border: '1px solid #e5e7eb', borderRadius: 8, background: '#fff', padding: 16, display: 'grid', gap: 12 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <h3 style={{ margin: 0, fontSize: 16, color: '#1f2937' }}>Roles</h3>
                            <Button type="primary" onClick={openCreatePanel}>
                                + Create New
                            </Button>
                        </div>

                        <div style={{ display: 'grid', gap: 8 }}>
                            {roles.map((r) => (
                                <div
                                    key={r.id}
                                    style={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        border: '1px solid #e5e7eb',
                                        borderRadius: 6,
                                        padding: '10px 12px',
                                        background: '#fff',
                                    }}
                                >
                                    <div style={{ display: 'grid', gap: 2 }}>
                                        <div style={{ fontWeight: 500 }}>{r.name} ({r.id})</div>
                                        <div style={{ fontSize: 12, color: '#6b7280' }}>{r.description || 'No description'}</div>
                                    </div>
                                    <div style={{ display: 'flex', gap: 8 }}>
                                        <Button onClick={() => handleEditRole(r.id)}>Edit</Button>
                                        <Popconfirm
                                            title="Delete this role?"
                                            onConfirm={() => handleDeleteRole(r.id)}
                                            okText="Delete"
                                            cancelText="Cancel"
                                        >
                                            <Button danger>Delete</Button>
                                        </Popconfirm>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default RoleManagementPage;
